// src/pages/VehicleFormPage.jsx
// Usata sia per aggiungere un nuovo veicolo che per modificarne uno esistente.
// Route: /garage/nuovo  oppure  /garage/:vehicleId/edit

import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { getVehicles, addVehicle, updateVehicle, getUsers, CATEGORIES } from '../lib/dataService';

const CATEGORIE = ['AUTO', 'MOTO', 'ALTRO'];
const CERTIFICAZIONI = ['', 'ASI ORO', 'ASI ARGENTO', 'FMI', 'FMI ORO', 'CRS', 'Altra'];
const ASSICURATORI   = ['', 'UNIPOL', 'SARA', 'PRIMA', 'AXA', 'Generali', 'Altra'];

const emptyForm = {
  categoria:            'AUTO',
  marca:                '',
  modello:              '',
  targa:                '',
  annoImmatricolazione: '',
  annoCostruzione:      '',
  certificazione:       '',
  assicuratore:         '',
  libretto:             false,
  proprietari:          [],   // nomi visualizzazione
  ownerIds:             [],   // uid Firebase
  note:                 '',
};

export default function VehicleFormPage() {
  const { vehicleId } = useParams();
  const isNew         = !vehicleId || vehicleId === 'nuovo';
  const { profile, isAdmin } = useAuth();
  const navigate      = useNavigate();

  const [form,    setForm]    = useState(emptyForm);
  const [users,   setUsers]   = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving,  setSaving]  = useState(false);
  const [errors,  setErrors]  = useState({});

  // Carica utenti (per selezione proprietari)
  useEffect(() => {
    if (isAdmin) getUsers().then(setUsers);
  }, [isAdmin]);

  // Carica veicolo esistente
  useEffect(() => {
    if (isNew || !profile) return;
    getVehicles(profile).then(vv => {
      const v = vv.find(x => x.id === vehicleId);
      if (v) setForm({
        categoria:            v.categoria            || 'AUTO',
        marca:                v.marca                || '',
        modello:              v.modello              || '',
        targa:                v.targa                || '',
        annoImmatricolazione: v.annoImmatricolazione || '',
        annoCostruzione:      v.annoCostruzione      || '',
        certificazione:       v.certificazione       || '',
        assicuratore:         v.assicuratore         || '',
        libretto:             v.libretto             || false,
        proprietari:          v.proprietari          || [],
        ownerIds:             v.ownerIds             || [],
        note:                 v.note                 || '',
      });
      setLoading(false);
    });
  }, [vehicleId, profile, isNew]);

  const set = (field, val) => {
    setForm(f => ({ ...f, [field]: val }));
    if (errors[field]) setErrors(e => ({ ...e, [field]: '' }));
  };

  const toggleOwner = (uid, nome) => {
    setForm(f => {
      const hasUid  = f.ownerIds.includes(uid);
      const hasNome = f.proprietari.includes(nome);
      return {
        ...f,
        ownerIds:    hasUid  ? f.ownerIds.filter(x => x !== uid)   : [...f.ownerIds, uid],
        proprietari: hasNome ? f.proprietari.filter(x => x !== nome) : [...f.proprietari, nome],
      };
    });
  };

  const validate = () => {
    const e = {};
    if (!form.marca.trim())   e.marca   = 'Campo obbligatorio';
    if (!form.modello.trim()) e.modello = 'Campo obbligatorio';
    if (form.ownerIds.length === 0) e.owners = 'Seleziona almeno un proprietario';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const data = {
        ...form,
        annoImmatricolazione: form.annoImmatricolazione ? parseInt(form.annoImmatricolazione) : null,
        annoCostruzione:      form.annoCostruzione      ? parseInt(form.annoCostruzione)      : null,
      };
      if (isNew) {
        const ref = await addVehicle(data);
        navigate(`/garage/${ref.id}`);
      } else {
        await updateVehicle(vehicleId, data);
        navigate(`/garage/${vehicleId}`);
      }
    } catch (e) {
      console.error(e);
      setSaving(false);
    }
  };

  if (loading) return <div style={S.loading}>Carico…</div>;

  return (
    <div style={S.page}>
      <div style={S.topbar}>
        <span style={S.back} onClick={() => navigate(isNew ? '/garage' : `/garage/${vehicleId}`)}>
          ← {isNew ? 'Garage' : 'Veicolo'}
        </span>
        <h1 style={S.title}>{isNew ? 'Aggiungi veicolo' : 'Modifica veicolo'}</h1>
        <span />
      </div>

      <div style={S.content}>

        {/* CATEGORIA */}
        <FormSection title="Categoria">
          <div style={S.catGrid}>
            {CATEGORIE.map(c => (
              <button
                key={c}
                style={{ ...S.catBtn, ...(form.categoria === c ? S.catBtnActive : {}) }}
                onClick={() => set('categoria', c)}
              >
                {c === 'AUTO' ? '🚗' : c === 'MOTO' ? '🏍' : '🚛'} {c}
              </button>
            ))}
          </div>
        </FormSection>

        {/* ANAGRAFICA */}
        <FormSection title="Anagrafica">
          <div style={S.grid2}>
            <Field label="Marca *" error={errors.marca}>
              <input style={inp(errors.marca)} value={form.marca}
                onChange={e => set('marca', e.target.value)} placeholder="es. Porsche" />
            </Field>
            <Field label="Modello *" error={errors.modello}>
              <input style={inp(errors.modello)} value={form.modello}
                onChange={e => set('modello', e.target.value)} placeholder="es. 993 Turbo" />
            </Field>
            <Field label="Targa">
              <input style={inp()} value={form.targa}
                onChange={e => set('targa', e.target.value.toUpperCase())}
                placeholder="es. AF894ZN"
                style={{ ...inp(), fontFamily: 'monospace', letterSpacing: 1 }} />
            </Field>
            <Field label="Anno immatricolazione">
              <input style={inp()} type="number" value={form.annoImmatricolazione}
                onChange={e => set('annoImmatricolazione', e.target.value)}
                placeholder="es. 1995" min="1900" max="2030" />
            </Field>
            <Field label="Anno costruzione">
              <input style={inp()} type="number" value={form.annoCostruzione}
                onChange={e => set('annoCostruzione', e.target.value)}
                placeholder="es. 1955" min="1900" max="2030" />
            </Field>
          </div>
        </FormSection>

        {/* CERTIFICAZIONE E ASSICURAZIONE */}
        <FormSection title="Certificazioni e assicurazione">
          <div style={S.grid2}>
            <Field label="Certificazione">
              <select style={inp()} value={form.certificazione}
                onChange={e => set('certificazione', e.target.value)}>
                {CERTIFICAZIONI.map(c => <option key={c} value={c}>{c || '— Nessuna —'}</option>)}
              </select>
            </Field>
            <Field label="Assicuratore">
              <select style={inp()} value={form.assicuratore}
                onChange={e => set('assicuratore', e.target.value)}>
                {ASSICURATORI.map(a => <option key={a} value={a}>{a || '— Nessuno —'}</option>)}
              </select>
            </Field>
          </div>
          <div style={{ marginTop: 14 }}>
            <label style={S.checkRow}>
              <input type="checkbox" checked={form.libretto}
                onChange={e => set('libretto', e.target.checked)} style={{ width: 18, height: 18 }} />
              <span style={S.checkLabel}>Libretto / carta di circolazione presente</span>
            </label>
          </div>
        </FormSection>

        {/* PROPRIETARI */}
        <FormSection title="Proprietari">
          {errors.owners && <div style={S.error}>{errors.owners}</div>}
          {isAdmin && users.length > 0 ? (
            <div style={S.ownerGrid}>
              {users.map(u => {
                const selected = form.ownerIds.includes(u.id);
                return (
                  <button
                    key={u.id}
                    style={{ ...S.ownerBtn, ...(selected ? S.ownerBtnActive : {}) }}
                    onClick={() => toggleOwner(u.id, u.nome || u.displayName)}
                  >
                    <div style={S.ownerName}>{u.nome || u.displayName}</div>
                    <div style={S.ownerEmail}>{u.email}</div>
                    {selected && <div style={S.ownerCheck}>✓</div>}
                  </button>
                );
              })}
            </div>
          ) : (
            <div style={{ color: '#888', fontSize: 14 }}>
              {isAdmin ? 'Carico utenti…' : 'I proprietari vengono assegnati dall\'admin.'}
            </div>
          )}
        </FormSection>

        {/* NOTE */}
        <FormSection title="Note">
          <textarea
            style={{ ...inp(), minHeight: 80, resize: 'vertical' }}
            value={form.note}
            onChange={e => set('note', e.target.value)}
            placeholder="Note aggiuntive, storia del veicolo, condizioni…"
          />
        </FormSection>

        {/* PULSANTI */}
        <div style={S.btnRow}>
          <button style={S.btnSec}
            onClick={() => navigate(isNew ? '/garage' : `/garage/${vehicleId}`)}>
            Annulla
          </button>
          <button
            style={{ ...S.btnPrimary, opacity: saving ? .6 : 1 }}
            onClick={handleSubmit}
            disabled={saving}
          >
            {saving ? 'Salvo…' : isNew ? '+ Aggiungi veicolo' : '✓ Salva modifiche'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── COMPONENTI HELPER ─────────────────────────────────────
function FormSection({ title, children }) {
  return (
    <div style={S.section}>
      <div style={S.secTitle}>{title}</div>
      {children}
    </div>
  );
}

function Field({ label, error, children }) {
  return (
    <div>
      <label style={S.label}>{label}</label>
      {children}
      {error && <div style={S.error}>{error}</div>}
    </div>
  );
}

const inp = (error) => ({
  width: '100%', padding: '11px 14px', fontSize: 15,
  border: `1.5px solid ${error ? '#c62828' : '#e0e0e0'}`,
  borderRadius: 10, outline: 'none', fontFamily: 'inherit',
  boxSizing: 'border-box', background: '#fafafa', color: '#1a1a1a',
});

// ── STYLES ────────────────────────────────────────────────
const S = {
  page:    { minHeight: '100vh', background: '#f5f4f0', fontFamily: "'Georgia', serif" },
  loading: { padding: 40, textAlign: 'center', color: '#888', fontSize: 18 },
  topbar:  { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', background: '#fff', borderBottom: '1px solid #e8e8e8' },
  back:    { fontSize: 15, cursor: 'pointer', color: '#185FA5' },
  title:   { fontSize: 18, fontWeight: 600 },
  content: { padding: 20, maxWidth: 680, margin: '0 auto' },
  section: { background: '#fff', borderRadius: 14, padding: '20px 22px', marginBottom: 14, border: '1px solid #e8e8e8' },
  secTitle:{ fontSize: 13, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '.7px', marginBottom: 16 },
  grid2:   { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 },
  label:   { display: 'block', fontSize: 13, fontWeight: 500, color: '#555', marginBottom: 6 },
  error:   { fontSize: 13, color: '#c62828', marginTop: 4 },
  catGrid: { display: 'flex', gap: 10 },
  catBtn:  { flex: 1, padding: '14px', borderRadius: 12, border: '2px solid #e0e0e0', background: '#fafafa', fontSize: 15, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit' },
  catBtnActive: { borderColor: '#1a1a1a', background: '#f0f0f0' },
  checkRow:{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' },
  checkLabel: { fontSize: 15 },
  ownerGrid:  { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px,1fr))', gap: 10 },
  ownerBtn:   { padding: '12px 14px', borderRadius: 10, border: '1.5px solid #e0e0e0', background: '#fafafa', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit', position: 'relative' },
  ownerBtnActive: { borderColor: '#1a1a1a', background: '#f5f5f5' },
  ownerName:  { fontSize: 15, fontWeight: 600, color: '#1a1a1a' },
  ownerEmail: { fontSize: 12, color: '#aaa', marginTop: 2 },
  ownerCheck: { position: 'absolute', top: 8, right: 10, fontSize: 14, color: '#2e7d32', fontWeight: 700 },
  btnRow:     { display: 'flex', gap: 10, marginTop: 8, marginBottom: 40 },
  btnPrimary: { flex: 1, padding: '14px', background: '#1a1a1a', color: '#fff', border: 'none', borderRadius: 10, fontSize: 16, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' },
  btnSec:     { flex: 1, padding: '14px', background: 'none', color: '#1a1a1a', border: '1.5px solid #e0e0e0', borderRadius: 10, fontSize: 16, cursor: 'pointer', fontFamily: 'inherit' },
};
